import React from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import SavedIndicator from 'src/components/questionnaire/SavedIndicator';

const Root = styled('div')({
  zIndex: 10,
  position: '-webkit-sticky',
  // eslint-disable-next-line no-dupe-keys
  position: 'sticky',
  bottom: 0,
  // Sticky footer: needs an opaque background to mask content scrolling
  // underneath. Match the light-grey page background (body --light-color) — NOT
  // white, and NOT the deployment's appearance colour (which is white on most
  // deployments and forced this bar white) — so the white form panels stand out
  // against it, consistent with the rest of the redesigned FHQ.
  backgroundColor: 'var(--light-color)',
  padding: '15px 0',
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  '@media (max-width: 600px)': {
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    padding: '10px'
  }
});

const NextButton = (props) => {
  const t = useAutoTranslation();

  // The primary (Continue / Submit) button. Rendered once via this helper so it
  // can sit either on the right (default, the FHQ's Back | Save | Continue
  // layout) or grouped next to Save in the centre (centerActions — used by
  // custom forms, which have no Back step and want Save + Submit together).
  const renderPrimary = () => (
    <Button
      disabled={props.disabled || props.loading}
      endIcon={
        props.loading ? <CircularProgress size="small" /> : <ArrowForwardIcon />
      }
      variant="contained"
      type={props.onClick ? 'button' : 'submit'}
      onClick={props.onClick}
      sx={{
        bgcolor: 'var(--color-active-fg)',
        '&:hover': { bgcolor: '#1f5018' }
      }}
    >
      {props.label ? t(props.label) : t('Save & Continue')}
    </Button>
  );

  return (
    <Root className="margin-top-4">
      {/* Left: Back */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'flex-start'
        }}
      >
        {props.onPreviousClick && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={props.onPreviousClick}
          >
            {t('back')}
          </Button>
        )}
      </Box>

      {/* Centre: Save & Continue Later + other secondary actions + saved indicator */}
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        {props.intermediateAction && (
          <Button
            color="secondary"
            variant="contained"
            type="button"
            endIcon={<SaveIcon />}
            onClick={props.intermediateAction}
          >
            {t(props.intermediateBtnLabel || 'Save & Return')}
          </Button>
        )}
        {props.onSaveClick && (
          <Button
            variant="contained"
            color="secondary"
            type="button"
            endIcon={<SaveIcon />}
            onClick={props.onSaveClick}
          >
            {t(props.saveBtnLabel || 'Save')}
          </Button>
        )}
        {props.onSavePDFCopy && (
          <Button
            onClick={props.onSavePDFCopy}
            color="secondary"
            variant="contained"
            endIcon={<DownloadIcon />}
          >
            {t('Download PDF Copy')}
          </Button>
        )}
        {/* centerActions: keep the primary (Submit) next to Save in the centre.
            Hidden on xs — the stacked mobile block below renders it there. */}
        {props.centerActions && (
          <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            {renderPrimary()}
          </Box>
        )}
        <SavedIndicator savedAt={props.savedAt} />
      </Box>

      {/* Right: Continue (default layout only; centerActions moves it to centre) */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'flex-end'
        }}
      >
        {!props.centerActions && renderPrimary()}
      </Box>

      {/* Mobile: stacked */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          alignItems: 'center'
        }}
      >
        {renderPrimary()}
        {props.onPreviousClick && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={props.onPreviousClick}
          >
            {t('back')}
          </Button>
        )}
      </Box>
    </Root>
  );
};

export default NextButton;
